import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { DataTableDirective } from 'angular-datatables';
import { Subject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from 'src/app/authentication/auth.service';
import { Usuario } from 'src/app/authentication/interfaces/interfaces';
import { LaboratorioService } from 'src/app/services/laboratorio/laboratorio.service';
import { OrdenesService } from 'src/app/services/transacciones/ordenes.service';
import { LanguageApp } from '../laboratorios/laboratorios.component';

interface SucursalTab {
  COD_SURC: string;
  DIR_SURC: string;
  pendientes?: number;
}

@Component({
  selector: 'app-resultados-lab',
  templateUrl: './resultados-lab.component.html',
  styleUrls: ['./resultados-lab.component.scss'],
  standalone: false
})
export class ResultadosLabComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild(DataTableDirective, { static: false }) dtElement!: DataTableDirective;
  @ViewChild('mdlComentario') mdlComentario!: TemplateRef<any>;

  dtOptions: DataTables.Settings = {};
  dtTrigger: Subject<any> = new Subject();
  isDtInitialized = false;

  public ordenes: any[] = [];
  public sucursales: SucursalTab[] = [];
  public spinnerBoolean = false;
  public cargandoSucursales = false;
  public isMobileView = false;

  private mobileTablesObserver?: MutationObserver;
  private mobileLabelsRaf?: number;

  public sucursalActiva: string = '';
  public fechaInicio: string = '';
  public fechaFin: string = '';
  public estado: string = 'facturadas';

  public comentario: string = '';
  public numOrdenComentario: string = '';
  public modalComentarioRef?: NgbModalRef;

  private _usuario!: Usuario;

  readonly TABS_ESPECIALES: SucursalTab[] = [
    { COD_SURC: 'DERIVADO',   DIR_SURC: 'DERIVADOS' },
    { COD_SURC: 'SINMUESTRA', DIR_SURC: 'SIN MUESTRA' },
    { COD_SURC: 'ENSUCURSAL', DIR_SURC: 'EN SUCURSAL' },
    { COD_SURC: 'ANULADAS',   DIR_SURC: 'ANULADAS' },
  ];

  constructor(
    private authService: AuthService,
    private laboratorioService: LaboratorioService,
    private toastr: ToastrService,
    private modalService: NgbModal,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    private ordenesService: OrdenesService,
  ) {}

  get token(): string { return localStorage.getItem('token') || ''; }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.detectMobileView();
  }

  ngOnInit(): void {
    this._usuario = this.authService.loginUsuario;
    this.detectMobileView();
    this.cargarSucursales();
  }

  ngAfterViewInit(): void {
    this.initializeMobileTables();
  }

  ngOnDestroy(): void {
    this.dtTrigger.unsubscribe();
    this.mobileTablesObserver?.disconnect();
    if (this.mobileLabelsRaf) {
      cancelAnimationFrame(this.mobileLabelsRaf);
    }
  }

  private detectMobileView(): void {
    if (typeof window === 'undefined') { this.isMobileView = false; return; }
    this.isMobileView = window.innerWidth <= 992;
  }

  private initializeMobileTables(): void {
    if (typeof window === 'undefined') return;
    this.updateMobileTableLabels();
    this.ngZone.runOutsideAngular(() => {
      if (typeof MutationObserver === 'undefined') return;
      this.mobileTablesObserver?.disconnect();
      this.mobileTablesObserver = new MutationObserver(() => this.updateMobileTableLabels());
      this.mobileTablesObserver.observe(this.elementRef.nativeElement, { childList: true, subtree: true });
    });
  }

  private updateMobileTableLabels(): void {
    if (typeof window === 'undefined') return;
    if (this.mobileLabelsRaf) cancelAnimationFrame(this.mobileLabelsRaf);
    this.mobileLabelsRaf = window.requestAnimationFrame(() => {
      const tables = Array.from(
        (this.elementRef.nativeElement as HTMLElement).querySelectorAll<HTMLTableElement>('.mobile-friendly-table')
      );
      tables.forEach(table => {
        const headers = Array.from(table.querySelectorAll<HTMLTableCellElement>('thead th'))
          .map(th => th.textContent?.trim() ?? '');
        table.querySelectorAll('tbody tr').forEach(row => {
          row.querySelectorAll<HTMLTableCellElement>('td').forEach((cell, i) => {
            const label = headers[i];
            if (label && cell.getAttribute('data-label') !== label) {
              cell.setAttribute('data-label', label);
            }
          });
        });
      });
    });
  }

  private cargarSucursales(): void {
    this.cargandoSucursales = true;
    this.authService.cargarSucursales('login', null, null, 'no')
      .subscribe((resp: any) => {
        const rows: any[] = Array.isArray(resp?.rows) ? resp.rows : [];
        const filtered = rows.filter(
          s => s.DIR_SURC !== 'BODEGA GENERAL' && s.DIR_SURC !== 'CC PLAZA ORELLANA'
        );
        this.sucursales = filtered.map(s => ({ COD_SURC: String(s.COD_SURC), DIR_SURC: s.DIR_SURC }));

        if (this.sucursales.length > 0) {
          this.sucursalActiva = this.sucursales[0].COD_SURC;
        }

        this.buildDtOptions();           // 1. configura dtOptions (con closure AJAX)
        this.cargandoSucursales = false; // 2. habilita el @if
        this.cdr.detectChanges();         // 3. renderiza @if → directiva monta y se suscribe a dtTrigger
        this.dtTrigger.next(null);        // 4. dispara → DataTable se crea + primer AJAX
        this.isDtInitialized = true;

        this.cargarPendientesTabs();
      });
  }

  private cargarPendientesTabs(): void {
    this.sucursales.forEach(s => {
      this.laboratorioService.pendientesLabSucursal(this.token, s.COD_SURC)
        .subscribe({
          next: (resp: any) => { s.pendientes = Number(resp?.total || 0); },
          error: () => { s.pendientes = 0; }
        });
    });
  }

  cambiarSucursal(codSucursal: string): void {
    this.sucursalActiva = codSucursal;
    if (this.isDtInitialized) {
      this.dtElement?.dtInstance?.then(dt => dt.ajax.reload());
    }
  }

  aplicarFiltros(): void {
    if (this.isDtInitialized) {
      this.dtElement?.dtInstance?.then(dt => dt.ajax.reload());
    }
  }

  private buildDtOptions(): void {
    this.dtOptions = {
      language: LanguageApp.spanish_datatables,
      pagingType: 'full_numbers',
      pageLength: 10,
      serverSide: true,
      processing: true,
      responsive: true,
      orderClasses: false,
      deferRender: true,
      searchDelay: 1500,
      ajax: (dtParams: any, callback) => {
        this.ngZone.run(() => { this.ordenes = []; this.spinnerBoolean = true; });

        this.laboratorioService.listarResultadosLab(this.token, {
          sucursal:    this.sucursalActiva,
          estado:      this.estado,
          fechaInicio: this.fechaInicio,
          fechaFin:    this.fechaFin,
          start:       dtParams.start,
          length:      dtParams.length,
          search:      dtParams.search?.value || '',
        }).subscribe({
          next: (resp: any) => {
            this.ngZone.run(() => {
              this.ordenes = Array.isArray(resp?.data) ? resp.data : [];
              this.spinnerBoolean = false;
              callback({
                recordsTotal:    resp?.recordsTotal    || 0,
                recordsFiltered: resp?.recordsFiltered || 0,
                data: [],
              });
            });
          },
          error: () => {
            this.ngZone.run(() => {
              this.ordenes = [];
              this.spinnerBoolean = false;
              callback({ recordsTotal: 0, recordsFiltered: 0, data: [] });
            });
          }
        });
      },
    };
  }

  /* ── Acciones ─────────────────────────────────────────────────────── */

  verOrden(orden: any): void {
    const pdfWindow = window.open('', '_blank');
    this.ordenesService.generarOrdenTransferencia(
      this.token,
      orden.NUM_ORD || '',
      orden.NOM_DOC || '',
      orden.CEL_DOC || ''
    ).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        if (pdfWindow) {
          pdfWindow.location.href = url;
        } else {
          window.open(url, '_blank');
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      },
      error: () => {
        pdfWindow?.close();
        this.toastr.error('No se pudo generar la orden digital.', 'Atención');
      }
    });
  }

  imprimirTicket(orden: any): void {
    const numeroOrden = String(orden?.NUM_ORD || '').trim();
    if (!numeroOrden) {
      this.toastr.error('No se encontró el número de orden para imprimir el ticket.', 'Atención');
      return;
    }

    const pdfWindow = window.open('', '_blank');
    this.ordenesService.generarTicketMuestraLaboratorio(
      this.token,
      numeroOrden,
      'nuevo',
      'Electronica',
      orden?.COD_ORD || ''
    ).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        if (pdfWindow) {
          pdfWindow.location.href = url;
        } else {
          window.open(url, '_blank');
        }
        setTimeout(() => URL.revokeObjectURL(url), 30000);
      },
      error: (err) => {
        pdfWindow?.close();
        this.toastr.error(
          err?.error?.message || err?.message || 'No se pudo generar el ticket de muestras.',
          'Atención'
        );
      }
    });
  }

  abrirComentario(orden: any): void {
    this.numOrdenComentario = orden.NUM_ORD;
    this.comentario = orden.OBS_ORD || '';
    this.modalComentarioRef = this.modalService.open(this.mdlComentario, { centered: true });
  }

  generarOrdenOrion(orden: any): void {
    this.toastr.info('Procesando orden en Orion...', '', { timeOut: 2500 });
    this.laboratorioService.generarOrdenOrion(this.token, orden.COD_ORD)
      .subscribe({
        next: () => {
          this.toastr.success('Orden enviada a Orion correctamente');
        },
        error: (err: any) => {
          this.toastr.error(err?.error?.message || 'No se pudo generar la orden en Orion');
        }
      });
  }

  guardarComentario(): void {
    this.laboratorioService.agregarComentarioLab(this.token, this.numOrdenComentario, this.comentario)
      .subscribe({
        next: () => {
          const row = this.ordenes.find(o => o.NUM_ORD === this.numOrdenComentario);
          if (row) row.OBS_ORD = this.comentario;
          this.toastr.success('Comentario guardado');
          this.modalComentarioRef?.close();
        },
        error: () => {
          this.toastr.error('No se pudo guardar el comentario');
        }
      });
  }

  orionError(respOrion: string): string | null {
    if (!respOrion) return null;
    try {
      const parsed = JSON.parse(respOrion);
      return parsed?.errors ? (parsed.message || 'Error en Orion') : null;
    } catch { return null; }
  }

  esTabEspecial(cod: string): boolean {
    return ['DERIVADO','SINMUESTRA','ENSUCURSAL','ANULADAS','PENDIENTE'].includes(cod);
  }

  get mostrarBtnResultados(): boolean {
    return this.estado === 'facturadas' && !this.esTabEspecial(this.sucursalActiva);
  }

  get mostrarBtnVerResultados(): boolean {
    return this.estado === 'con-resultados';
  }

  get mostrarBtnAnular(): boolean {
    return this.estado === 'facturadas';
  }
}
